/**
 * Bee Natural — Animation Controller v3
 * Source of truth: lavble.config 0.0 / src/
 *
 * Modules:
 *   1. Header scroll  — .bn-scrolled at scrollY > 80 (Navigation.tsx)
 *   2. Scroll reveal  — per-element, staggered (useScrollReveal.ts)
 *   3. Product cards  — hover glow (CollectionSection.tsx)
 *   4. Collection cards — gold hover (auto-inject .bn-collection-card)
 *   5. Footer hover   — partial brand-name highlight (Footer.tsx)
 */

(function () {
  'use strict';

  /* =================================================================
     1. HEADER SCROLL
     Prototype: Navigation.tsx
       const [scrolled, setScrolled] = useState(false)
       useEffect → scrollY > 80 → setScrolled(true)
       className: scrolled ? "bg-background/90 backdrop-blur-md py-4"
                           : "bg-transparent py-8"
       transition-all duration-700

     Shopify: JS adds/removes .bn-scrolled on header-component[transparent]
              CSS handles all visual transitions (bg, blur, padding).
  ================================================================= */
  function initHeaderScroll() {
    var header = document.querySelector('header-component[transparent]');
    if (!header) return;

    var rafId = null;

    function update() {
      rafId = null;
      var y = window.scrollY;
var on = header.classList.contains('bn-scrolled');
if (!on && y > 80) header.classList.add('bn-scrolled');
else if (on && y < 40) header.classList.remove('bn-scrolled');
    }

    function onScroll() {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(update);
    }

    update(); // set correct class on page load (e.g. if user refreshes mid-page)
    document.addEventListener('scroll', onScroll, { passive: true });
  }


  /* =================================================================
     2. SCROLL REVEAL — Per Element
     Prototype: useScrollReveal.ts + .reveal / .reveal.visible
       observer: threshold 0.15, rootMargin '0px 0px -50px 0px'
       Each text block has its own .reveal + inline transitionDelay

     Shopify approach:
       • Explicit:  .bn-reveal already in liquid templates (product-list,
                    media-with-content) with inline transition-delay
       • Auto:      Scans each section for individual text elements
                    (h2, h3, p, .rte, labels) and injects .bn-reveal
                    with staggered delays — mirrors prototype's per-element
                    animation pattern

     Trigger point:
       threshold: 0   — fires on first visible pixel (avoids tall-section
                         maths that broke -800px rootMargin approach)
       rootMargin: '0px 0px -20% 0px'
                       — shrinks effective viewport bottom by 20%
                         → elements animate when they enter top 80% of screen
                         → visually: element is well in view before animating
  ================================================================= */
  function initScrollReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.bn-reveal, .bn-section-reveal').forEach(function (el) {
        el.classList.add('bn-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('bn-visible');
            observer.unobserve(entry.target); // fire once, then release
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -20% 0px',
      }
    );

    // A) Observe explicit .bn-reveal elements already in liquid
    document.querySelectorAll('.bn-reveal:not(.bn-visible)').forEach(function (el) {
      observer.observe(el);
    });

    // B) Auto-inject: find individual text elements within each section
    //    and animate them independently with staggered delays.
    //    Mirrors prototype where every <div class="reveal"> has its own
    //    style={{ transitionDelay: '0.2s' }} etc.
    var skipSelector = [
      '#header-group',
      '.shopify-section-group-header-group',
      '.shopify-section-group-footer-group',
      'footer',
    ].join(', ');

    document.querySelectorAll('.shopify-section').forEach(function (section) {
      if (section.closest(skipSelector)) return;
      if (section.id && (section.id.includes('header') || section.id.includes('footer'))) return;
      if (section.querySelector('.hero')) return; // hero has its own CSS animations

      var elements = collectAnimatableElements(section);

      elements.forEach(function (el, i) {
        el.classList.add('bn-reveal');
        // Stagger: 0s, 0.15s, 0.3s … capped at 0.6s
        el.style.transitionDelay = Math.min(i * 0.15, 0.6) + 's';
        observer.observe(el);
      });
    });
  }

  /* =================================================================
     collectAnimatableElements — DOM tree-walker
     ─────────────────────────────────────────────────────────────────
     Instead of a hardcoded selector list (which misses elements),
     this walks the real DOM tree and finds EVERY element that:
       • Is a semantic text tag (h1–h6, p, li, blockquote …)      → animate
       • Is a web component with text (marquee-component …)        → animate
       • Has DIRECT text-node children and no block descendants    → animate
         (catches: <div class="label">OUR PHILOSOPHY</div>,
                   <div class="flame-text">Wax softens.</div>)
       • Is a wrapper with only block children                     → recurse

     Does NOT animate: scripts, images, buttons, SVG, hidden els.
     Does NOT double-animate: stops recursing once a node is queued.
  ================================================================= */
  function collectAnimatableElements(container) {
    var results = [];

    // Semantic text tags — always animate as a unit
    var ALWAYS = {
      H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1,
      P: 1, LI: 1, BLOCKQUOTE: 1, FIGCAPTION: 1, DT: 1, DD: 1,
    };

    // Tags to skip entirely (non-content)
    var SKIP = {
      SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1,
      SVG: 1, PATH: 1, CIRCLE: 1, RECT: 1, LINE: 1, POLYGON: 1,
      USE: 1, DEFS: 1, G: 1, SYMBOL: 1,
      IMG: 1, VIDEO: 1, AUDIO: 1, PICTURE: 1, SOURCE: 1, CANVAS: 1,
      INPUT: 1, TEXTAREA: 1, SELECT: 1, OPTION: 1, OPTGROUP: 1,
      BUTTON: 1, FORM: 1, LABEL: 1,
      BR: 1, HR: 1, WBR: 1,
      LINK: 1, META: 1,
    };

    // Returns true if el has at least one text node child with real content
    function hasDirectText(el) {
      var nodes = el.childNodes;
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].nodeType === 3 && nodes[i].textContent.trim().length > 1) {
          return true;
        }
      }
      return false;
    }

    // Returns true if el contains any block-level descendants (heading or p)
    // Used to detect pure wrapper divs that should be recursed into, not animated
    function hasBlockDescendants(el) {
      return !!(el.querySelector('h1,h2,h3,h4,h5,h6,p,ul,ol'));
    }

    function walk(el) {
      // Gate checks
      if (!el || el.nodeType !== 1) return;
      if (SKIP[el.tagName]) return;
      if (el.classList.contains('bn-reveal')) return;
      if (el.classList.contains('bn-visible')) return;
      if (el.classList.contains('bn-section-reveal')) return;
      if (el.classList.contains('hero') || el.closest('.hero')) return;

      // Skip elements INSIDE collection cards and product cards.
      // Cards animate as a unit (lift + glow). Their inner text elements
      // (title, description) must stay visible at all times — applying
      // opacity:0 via bn-reveal breaks their visibility.
      if (el.closest('.collection-card') ||
          el.closest('.product-card') ||
          el.closest('product-card') ||
          el.closest('.card-wrapper')) return;

      // Web components (custom elements) — animate as a single unit
      if (el.tagName.indexOf('-') !== -1) {
        if (el.textContent.trim().length > 0) {
          results.push(el);
        }
        return;
      }

      // Semantic text elements — always animate (h1–h6, p, li …)
      if (ALWAYS[el.tagName]) {
        if (el.textContent.trim().length > 0) {
          results.push(el);
        }
        return; // don't recurse inside paragraphs / headings
      }

      // Non-semantic elements (div, section, span, article …):
      // Animate if they DIRECTLY hold text AND have no block descendants
      // → catches label divs, short phrase divs, eyebrow text, etc.
      if (hasDirectText(el) && !hasBlockDescendants(el)) {
        results.push(el);
        return;
      }

      // Otherwise it's a wrapper — recurse into children
      for (var i = 0; i < el.children.length; i++) {
        walk(el.children[i]);
      }
    }

    for (var i = 0; i < container.children.length; i++) {
      walk(container.children[i]);
    }

    return results;
  }


  /* =================================================================
     3. PRODUCT CARDS
     Prototype: CollectionSection.tsx
       className="product-card group cursor-pointer"
       style={{ transitionDelay: `${0.2 + i * 0.2}s` }}
       group-hover: image scale-105 (transition-transform duration-1000)
       card: box-shadow 0.6s ease, translateY(-4px)
  ================================================================= */
  function initProductCards() {
    document.querySelectorAll([
      'product-card',
      '.product-card',
      '.card-wrapper',
    ].join(', ')).forEach(function (card) {
      card.classList.add('bn-product-card');
    });
  }


  /* =================================================================
     4. COLLECTION CARDS — gold hover
     Auto-injects .bn-collection-card.
     CSS handles: gold glow shadow + subtle lift + title color.
  ================================================================= */
  function initCollectionCards() {
    document.querySelectorAll([
      '.collection-card',
      '.collection-list__item',
      '.card-wrapper--collection',
    ].join(', ')).forEach(function (card) {
      card.classList.add('bn-collection-card');
    });
  }


  /* =================================================================
     5. FOOTER HOVER — partial brand name highlight
     Prototype: Footer.tsx has no specific partial hover,
     but the user spec requires: "© 2026 Bee Natural by Viktoria"
     → only "Bee Natural by Viktoria" highlights on hover.

     Approach: JS finds the copyright line and wraps the brand name
     in <span class="bn-hover-gold"> → CSS handles gold color on hover.
  ================================================================= */
  function initFooterHover() {
    var footerSelectors = [
      '.footer__copyright',
      '.footer__bottom p',
      'footer [class*="copyright"]',
      '.footer__legal',
    ].join(', ');

    document.querySelectorAll(footerSelectors).forEach(function (el) {
      // Match "Bee Natural by [Name]" pattern and wrap in hover span
      if (el.querySelector('.bn-hover-gold')) return; // already done
      el.innerHTML = el.innerHTML.replace(
        /(Bee Natural by \S+)/g,
        '<span class="bn-hover-gold">$1</span>'
      );
    });
  }


  /* =================================================================
     Boot
  ================================================================= */
  function init() {
    initHeaderScroll();
    initScrollReveal();
    initProductCards();
    initCollectionCards();
    initFooterHover();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when Shopify injects sections in the theme editor
  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
    initProductCards();
    initCollectionCards();
    initFooterHover();
  });

  
})();
